import OverflowableContent from '@creator/ui/components/OverflowableContent/OverflowableContent';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import EditorViewOnly from '@src/basic-components/EditorViewOnly/EditorViewOnly';
import Poll from '@src/components/Poll/Poll/Poll';
import { useStoreState } from '@src/model/hooks';
import { parsePostContent } from '@src/model/upvote/helpers';
import { canViewPostDetails } from '@src/model/upvote/permission';
import { isPostPublished } from '@src/store/posts/helpers/posts';
import { usePost } from '@src/store/posts/hooks/usePost';
import { isTextBasedBlockType } from '@src/utils/editorjs-utils/editorjs-utils';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface PostContentProps {
    className?: string;
    postId: string;
    tokenName: string;
    enableExpend?: boolean;
    onExpand?: () => void;
}

const MAX_CONTENT_HEIGHT_PX = 600;

const PostContent: React.FC<PostContentProps> = props => {
    const { className, postId, tokenName, onExpand } = props;

    // Hooks
    const post = usePost(postId);
    const isPublished = isPostPublished(post);
    const isAuthenticating = useStoreState(state => state.user.isAuthenticating);

    const { t } = useTranslation();
    const editorViewId = useMemo(() => `post-editor-content-${postId}`, [postId]);
    const postContent = useMemo(() => post && parsePostContent(post), [post]);
    const firstBlock = useMemo(() => postContent?.blocks?.[0], [postContent]);
    const isFirstBlockTextBased = useMemo(() => firstBlock && isTextBasedBlockType(firstBlock.type), [firstBlock]);

    // Permissions check
    if (!isPublished && !isAuthenticating && !canViewPostDetails(tokenName, postId)) return <Text className="block mt-4 text-sm">{t(`postStatus${post?.status}`)}</Text>;

    function renderPoll() {
        if (!post?.pollOptions || !post?.pollOptions?.length) return null;
        return <Poll tokenName={tokenName} postId={postId} className="pt-4 mb-4" />;
    }

    return (
        <div className={cn('space-y-4', className)}>
            {renderPoll()}
            <OverflowableContent
                readMoreText={t('postContent.expendButton')}
                onExpand={onExpand}
                contentId={editorViewId}
                enableExpend={isFirstBlockTextBased}
                maxHeight={MAX_CONTENT_HEIGHT_PX}
            >
                <EditorViewOnly tokenName={tokenName} postId={postId} />
            </OverflowableContent>
        </div>
    );
};

export default memo(PostContent);
